<template>
  <div :id="anchorId" class="form-group">
    <div v-if="showToggle" class="passwordWrapper">
      <Field
        :type="isVisible ? 'text' : 'password'"
        :name="name"
        :placeholder="placeholder"
        :rules="validationRules"
        class="form-control"
        autocomplete="new-password"
      />
      <img
        :src="isVisible ? require('@/assets/img/eye-view.png') : require('@/assets/img/eye-hide.png')"
        class="eyePassword"
        @click="toggleVisibility"
       alt="voir">
    </div>
    <Field
      v-else
      type="password"
      :name="name"
      :placeholder="placeholder"
      :rules="validationRules"
      class="form-control"
      autocomplete="new-password"
    />
    <ErrorMessage :name="name" class="error-feedback"/>
  </div>
</template>

<script setup>
import {ref, computed} from 'vue';
import {Field, ErrorMessage} from "vee-validate";
import * as yup from "yup";

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: "Mot de passe*"
  },
  showToggle: {
    type: Boolean,
    default: false
  },
  anchorId: {
    type: String,
    default: null
  },
  isConfirmation: {
    type: Boolean,
    default: false
  },
  passwordFieldName: {
    type: String,
    default: 'password'
  },
  scrollOnValidation: {
    type: Function,
    default: null
  }
});

const isVisible = ref(false);
const allowedSpecialChars = ['$', '!', '#', '?', '-', '_'];

const validationRules = computed(() => {
  if (props.isConfirmation) {
    return (value, context) => {
      // Validation requise
      if (!value) {
        return "Le mot de passe est nécessaire";
      }

      // Récupérer la valeur du champ password depuis le contexte du formulaire
      const passwordValue = context.form[props.passwordFieldName];

      // Vérifier que les mots de passe correspondent
      if (value !== passwordValue) {
        return 'Mot de passe invalide : différent du précédent';
      }

      // Test personnalisé pour scroll
      if (props.scrollOnValidation && props.anchorId) {
        props.scrollOnValidation(props.anchorId, value);
      }

      return true;
    };
  }

  // Validation du mot de passe
  const allowedSpecialCharsDisplay = allowedSpecialChars.join(' ');

  // Construire le regex dynamiquement avec les caractères spéciaux autorisés
  const specialCharsPattern = allowedSpecialChars.map(char => {
    // Échapper les caractères spéciaux regex
    return char.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
  }).join('');
  const minimumOneRegex = new RegExp(`^(?=.*[a-zA-Z])(?=.*\\d)(?=.*[${specialCharsPattern}]).*$`);

  // Regex pour vérifier que seuls les caractères autorisés sont présents
  const allowedCharsOnlyRegex = new RegExp(`^[a-zA-Z0-9${specialCharsPattern}]+$`);

  let schema = yup
    .string()
    .required("Le mot de passe est nécessaire")
    .min(6, "Mot de passe invalide : minimum 6 caractères")
    .max(40, "Doit comporter au maximum 40 caractères")
    .matches(allowedCharsOnlyRegex, `Mot de passe invalide : seuls les lettres (sans accent), chiffres et caractères spéciaux (${allowedSpecialCharsDisplay}) sont autorisés`)
    .matches(minimumOneRegex, `Mot de passe invalide : au moins une lettre (sans accent), un chiffre et un caractère spécial (${allowedSpecialCharsDisplay})`);

  // Test personnalisé pour scroll
  if (props.scrollOnValidation && props.anchorId) {
    schema = schema.test('custom-validation', "Ce champ doit être renseigné", (value) => {
      props.scrollOnValidation(props.anchorId, value);
      return true;
    });
  }

  return schema;
});

const toggleVisibility = () => {
  isVisible.value = !isVisible.value;
};
</script>

<style scoped>
/* Les styles sont déjà définis dans _connexion.scss */
</style>
